import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PORTAL_ABOVE_ALL_PANELS_Z } from "../../utils/panelZIndex";
import { PALETTE_GROUPS } from "./paletteGroups";
import { BtNodeDisc, CATEGORY_COLORS } from "./nodeAppearance";
import InfoTooltip from "../ui/InfoTooltip";
import { useIsMobile } from "../../hooks/useIsMobile";

const POPOVER_WIDTH = 240;
const POPOVER_MAX_HEIGHT = 360;
const MARGIN = 8;

function useAnchoredPosition(anchorRect, size) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useLayoutEffect(() => {
    if (!anchorRect) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchorRect.left + anchorRect.width / 2 - size.width / 2;
    left = Math.max(MARGIN, Math.min(left, vw - size.width - MARGIN));
    let top = anchorRect.bottom + MARGIN;
    if (top + size.height > vh - MARGIN) {
      const above = anchorRect.top - size.height - MARGIN;
      top = above >= MARGIN ? above : Math.max(MARGIN, vh - size.height - MARGIN);
    }
    setPos({ top, left });
  }, [anchorRect, size.width, size.height]);
  return pos;
}

/**
 * Grouped palette popover anchored at a slot's bounding rect.
 * Calls `onPick(kind, insertOverrides?)` when a kind is chosen, then `onClose`.
 */
export default function InsertPopover({ anchorRect, onPick, onClose, groups = PALETTE_GROUPS }) {
  const isMobile = useIsMobile();
  const containerRef = useRef(null);
  const [query, setQuery] = useState("");

  const pos = useAnchoredPosition(anchorRect, {
    width: POPOVER_WIDTH,
    height: POPOVER_MAX_HEIGHT,
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    const onDown = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.label.toLowerCase().includes(q) ||
            it.key.toLowerCase().includes(q) ||
            (it.paletteRowId && it.paletteRowId.toLowerCase().includes(q)) ||
            (it.help && it.help.toLowerCase().includes(q))
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, query]);

  const node = (
    <div
      ref={containerRef}
      className="fixed flex flex-col overflow-hidden rounded-lg border border-gray-700 shadow-2xl"
      style={{
        top: pos.top,
        left: pos.left,
        width: POPOVER_WIDTH,
        maxHeight: POPOVER_MAX_HEIGHT,
        zIndex: PORTAL_ABOVE_ALL_PANELS_Z,
        backgroundColor: "rgba(22, 23, 25, 0.98)",
        backdropFilter: "blur(8px)",
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="border-b border-gray-800 px-2 py-2">
        <input
          autoFocus={!isMobile}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search steps..."
          className="w-full rounded-sm border border-gray-700 bg-gray-900/80 px-2 py-1 text-xs text-gray-100 focus:border-sky-500 focus:outline-none"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-1 py-1">
        {filteredGroups.length === 0 ? (
          <div className="px-2 py-3 text-[11px] italic panel-text-helper">No matches.</div>
        ) : (
          filteredGroups.map((g) => (
            <div key={g.key} className="mb-1">
              <div
                className="flex items-center gap-1.5 px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{ color: CATEGORY_COLORS[g.key] || CATEGORY_COLORS.default }}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: CATEGORY_COLORS[g.key] || CATEGORY_COLORS.default }}
                  aria-hidden="true"
                />
                <span>{g.title}</span>
              </div>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <div
                    key={it.paletteRowId || it.key}
                    className="group flex w-full items-center gap-2 rounded-sm pr-2 text-[11px] text-gray-200 transition-colors hover:bg-gray-700/60 focus-within:bg-gray-700/60"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onPick(it.key, it.insertOverrides);
                        onClose();
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 rounded-sm px-2 py-1 text-left focus:outline-none"
                    >
                      <BtNodeDisc kind={it.key} size={22} />
                      <span className="flex-1 truncate">{it.label}</span>
                    </button>
                    {it.help ? (
                      <span
                        className="shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <InfoTooltip text={it.help} widthClass="w-72" />
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
